/**
 * Client Types (TASK-137)
 *
 * Type definitions for Client Management UI.
 */
import type { ManagerBrief } from './manager';
import type { LeadAttributeType } from './settings';
import type { NextBotTouch } from './nextBotTouch';
import type { ClientCompanyBrief } from './clientCompany';

/** KAN-783: значение настроенного атрибута лида в карточке клиента (null: не выяснено). */
export interface ClientLeadAttribute {
  id: string;
  name: string;
  type: LeadAttributeType;
  value: string | null;
}

export interface Client {
  id: string;
  phone: string;
  name: string;
  telegram_id: number | null;
  email?: string;
  is_identified: boolean;
  /** KAN-723: own (test) contact: hidden from "Требуют внимания", not counted in the funnel. */
  is_own?: boolean;
  /** KAN-898: moved to junk at (ISO); null or absent: not junk. */
  junk_at?: string | null;
  notes: string;
  /** KAN-911: когда взят в работу (ISO, detail clients/<id>/); null: не взят. */
  taken_at?: string | null;
  estimates_count: number;
  /** KAN-616: messages in the client's dialog (list endpoint). */
  messages_count?: number;
  /** KAN-616: client wrote at least one message. */
  has_user_reply?: boolean;
  /** KAN-646: time of the latest dialog message (list endpoint); null: no messages. */
  last_message_at?: string | null;
  created_at: string;
  updated_at: string;
  followup_at?: string | null;
  followup_note?: string;
  /**
   * KAN-915: next contact of the lead (list endpoint; same as the CRM "Следующая
   * связь" column: followup_at, else the nearest pending scheduled call); null: none.
   */
  next_contact_at?: string | null;
  /**
   * KAN-918: last client message newer than taking to work and the last manual
   * manager message (ISO); null: none; absent on an older backend.
   */
  new_message_at?: string | null;
  /** KAN-857: client Telegram avatar url (needs the token); "" none; absent on an older backend. */
  avatar_url?: string;
  /**
   * KAN-714: kind of the scheduled next step (codename): "followup_message" |
   * "callback" | "visit" | other; "" when not set. Absent on an older backend.
   */
  followup_type?: string;
  /** KAN-714: Russian label of `followup_type` ("Написать", "Созвон", ...); null when empty. */
  followup_type_label?: string | null;
  stage?: { id: number; label: string; order: number } | null;
  outcome?: 'open' | 'won' | 'lost';
  /**
   * TASK-340: real per-funnel positions of the client. One entry per funnel
   * the client participates in. Supersedes the legacy `stage` field for the
   * funnel widget; `stage` is kept for backward compatibility (list views,
   * legacy clients without positions).
   */
  funnel_positions?: ClientFunnelPosition[];
  /**
   * KAN-618: OUR Telegram account that talks to the client (list endpoint).
   * null: no Telegram dialog; absent on an older backend.
   */
  sender_account?: ClientSenderAccount | null;
  /** KAN-626: bot is silent, the operator writes to the client by hand. */
  manual_mode?: boolean;
  manual_mode_at?: string | null;
  /** KAN-686: whether the bot is silent and why (detail endpoint only). */
  bot_silence?: BotSilence;
  /** KAN-632: Telegram username without "@" (null: unknown). */
  telegram_username?: string | null;
  /** KAN-632: decision-maker contacts from lead_info. */
  lead_contacts?: LeadContact[];
  /** KAN-639: company from lead_info ("Компания" key in any case); null: unknown. */
  company?: string | null;
  /** KAN-826: organization the client belongs to; null: none. KAN-851: the list row has {id, name}. */
  client_company?: ClientCompanyBrief | null;
  /**
   * KAN-826: own fields of the contact from list imports (detail endpoint):
   * {name: {value, to_bot}}. Read-only in the UI.
   */
  profile_data?: Record<string, unknown>;
  /**
   * KAN-682: where the client came from. Code: "" (unknown) | "outreach_telegram"
   * | "cold_call" | "inbound_telegram" | "inbound_call" | "referral" | "manual"
   * | "inbound_email" (KAN-724: клиент создан из письма в ящик).
   */
  source?: string;
  /** KAN-682: Russian label of `source` ("Неизвестно" when the source is not recorded). */
  source_label?: string;
  /** KAN-744: manager of the client; null: not assigned; absent on an older backend. */
  manager?: ManagerBrief | null;
  /** KAN-783: what the bot found out (configured attributes); absent on an older backend. */
  lead_attributes?: ClientLeadAttribute[];
  /** KAN-810: lead temperature hot | warm | cool; "" none. Absent on an older backend. */
  lead_temperature?: string;
  /** KAN-810: who set it: bot | manual | backfill; "" none. */
  lead_temperature_source?: string;
  /** KAN-810: why this level (what the client said). */
  lead_temperature_reason?: string;
  /**
   * KAN-897: stored temperature even for a closed lead (lead_temperature is ""
   * for won/lost); absent on an older backend.
   */
  lead_temperature_stored?: string;
  /** KAN-897: when the outcome was set (ISO); null: open. Absent on an older backend. */
  outcome_at?: string | null;
  /** KAN-810: when the lead was marked as handed over (ISO); null: not handed over. */
  transferred_at?: string | null;
  /** KAN-810: to whom the lead was handed over. */
  transferred_to?: string;
  /** KAN-815: the next bot touch; null: none planned; absent on an older backend. */
  next_bot_touch?: NextBotTouch | null;
  /** KAN-834: CRM comment (detail endpoint, read-only; edited via PATCH crm/clients/{id}/). */
  crm_comment?: string;
  /** KAN-852: client dossier (goal, orders), written by the bot; detail endpoint, read-only. */
  summary?: string;
}

/** KAN-686: reason the bot does not answer the client. */
export type BotSilenceReason = 'manual_mode' | 'handed_to_human';

/**
 * KAN-686: bot silence of a client: manual mode or handed to a manager
 * (needs_human on the handed_to_manager stage). label is the Russian caption.
 */
export interface BotSilence {
  silent: boolean;
  reason: BotSilenceReason | null;
  label: string | null;
}

/** KAN-682: value of the "Источник" filter for clients without a recorded source. */
export const CLIENT_SOURCE_UNKNOWN = 'unknown';

/**
 * KAN-682: options of the "Источник" filter (code -> Russian label), in UI order.
 * The codes match `Client.source`; "unknown" is the filter-only value for an
 * empty source.
 */
export const CLIENT_SOURCE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'outreach_telegram', label: 'Рассылка Telegram' },
  { value: 'cold_call', label: 'Холодный обзвон' },
  { value: 'inbound_telegram', label: 'Входящее в Telegram' },
  { value: 'inbound_call', label: 'Входящий звонок' },
  { value: 'referral', label: 'Рекомендация' },
  { value: 'manual', label: 'Создан вручную' },
  { value: 'inbound_email', label: 'Входящее письмо' },
  { value: CLIENT_SOURCE_UNKNOWN, label: 'Неизвестно' },
];

/** KAN-632: decision-maker contact (empty fields are ""; telegram without "@"). */
export interface LeadContact {
  name: string;
  role: string;
  telegram: string;
  phone: string;
  email: string;
}

/** KAN-631/639: kind of link between two client cards. */
export type ClientLinkKind = 'same_company' | 'same_person' | 'referred_by' | string;

/**
 * KAN-631/639: link status ("rejected" links are not returned by the list).
 * KAN-667: links are confirmed on creation; "suggested" is legacy only.
 */
export type ClientLinkStatus = 'suggested' | 'confirmed' | 'rejected';

/** KAN-639: brief card of the linked client. */
export interface LinkedClientBrief {
  id: string;
  name: string;
  /** Role of the linked person ("" when unknown). */
  role: string;
  phone: string;
  email: string;
  company: string;
  stage: string;
}

/** KAN-631: GET /clients/{id}/links/ item, seen from that client. */
export interface ClientLink {
  id: string;
  kind: ClientLinkKind;
  kind_label: string;
  status: ClientLinkStatus;
  source: string;
  direction: 'in' | 'out';
  evidence: Record<string, unknown>;
  created_at: string;
  decided_at: string | null;
  client: LinkedClientBrief;
}

/** KAN-631/667: "reject" cancels a link of any status; "confirm" is kept for compatibility. */
export type ClientLinkDecision = 'confirm' | 'reject';

/**
 * KAN-665: GET /clients/{id}/lead-card/ - the lead card (what the manager gets
 * by e-mail) as a structure. Unknown fields of company/contact are absent;
 * promised/notes/source are "" when there is nothing to say.
 */
export interface LeadCard {
  subject: string;
  title: string;
  date: string;
  source: string;
  action: { title: string; steps: string[] };
  company: Partial<Record<'name' | 'address' | 'phone' | 'site' | 'email', string>>;
  contact: Partial<Record<'name' | 'role' | 'phone' | 'telegram' | 'email', string>>;
  conversation: { stamp: string; who: string; text: string }[];
  promised: string;
  notes: string;
  /**
   * KAN-686: the client's messages after the hand-off to a manager (up to 10
   * latest, oldest first); empty when there are none.
   */
  after_handoff?: { stamp: string; text: string }[];
}

/** KAN-690: response of POST /clients/{id}/lead-card/send/. */
export interface SendLeadCardResponse {
  sent_to: string[];
}

/** KAN-626: response of POST /clients/{id}/manual-mode/. */
export interface ManualModeResponse {
  manual_mode: boolean;
  manual_mode_at: string | null;
}

/** KAN-626: response of POST /clients/{id}/messages/ (send queued). */
export interface SendClientMessageResponse {
  job_id: string;
  status: string;
  account_id: string;
}

/** KAN-912: where our message is deleted: at the client too or only in history. */
export type DeleteClientMessageScope = 'client' | 'history';

/** KAN-912: answer of DELETE clients/<id>/messages/<message_id>/. */
export interface DeleteClientMessageResponse {
  /** The message is gone from the system history. */
  deleted_in_history: boolean;
  /** The message was deleted in the client's Telegram. */
  revoked: boolean;
  /** The client no longer had the message (nothing to delete there). */
  not_found_at_client: boolean;
  /** Why something did not work out; empty on full success. */
  reason: string;
  /** The message was still queued: sending was cancelled and the record deleted. */
  cancelled_before_send?: boolean;
}

/** KAN-618: our Telegram account in the clients list. */
export interface ClientSenderAccount {
  account_id: string;
  label: string;
  phone: string;
  username: string;
  persona_name: string;
  /** Name from the Telegram profile; "" when not read yet. */
  profile_name: string;
  /** Plain URL or an /api/... path (then loaded with the token); "" if none. */
  avatar_url: string;
}

/**
 * TASK-340: client's position in ONE funnel, as serialized by the detail
 * ClientSerializer (`funnel_positions` field).
 *
 * `stage` is null for SET_NULL survivors: the client is in the funnel but the
 * stage it pointed to was deleted / not yet assigned.
 */
export interface ClientFunnelPosition {
  funnel: {
    id: number | string;
    codename: string;
    name: string;
  };
  stage: {
    id: number | string;
    codename: string;
    label: string;
    order: number;
  } | null;
}

export interface ClientFilters {
  search?: string;
  /** KAN-618: account_id of our Telegram account that talks to the client. */
  sender_account?: string;
  is_identified?: boolean;
  page?: number;
  page_size?: number;
  /** KAN-616: true = client replied, false = never replied; absent = all. */
  replied?: boolean;
  /** KAN-616: funnel stage codename. */
  stage?: string;
  funnel?: string;
  /** KAN-682: client source code; "unknown" = source not recorded. */
  source?: string;
  /** KAN-734: 1 = only clients marked "Отработано" without an outcome yet. */
  worked?: 1;
  /** KAN-737: client outcome (open / won / lost); KAN-897: worked = won + lost; absent = all. */
  outcome?: 'open' | 'won' | 'lost' | 'worked';
  /** KAN-915: future = next contact later than now; due = none or already due. */
  next_contact?: 'future' | 'due';
  /** KAN-897: server ordering, e.g. "-outcome_at". */
  ordering?: string;
  /** KAN-744: manager id, or "none" = clients without a manager. */
  manager?: string;
  /** KAN-788: lead attribute id; alone = clients with any filled value. */
  attr?: string;
  /** KAN-788: attribute value (with `attr`). */
  attr_value?: string;
}

export interface PaginatedClientsResponse {
  results: Client[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface CreateClientPayload {
  phone: string;
  name?: string;
  email?: string;
  notes?: string;
}

export interface UpdateClientPayload {
  phone?: string;
  name?: string;
  email?: string;
  notes?: string;
  telegram_id?: string | number | null;
  /**
   * KAN-806: Telegram username; "" clears it. The server normalizes it and
   * answers 400 on an invalid value (then nothing of the request is saved).
   */
  telegram_username?: string;
  /** KAN-723: mark the client as an own (test) contact. */
  is_own?: boolean;
  /** KAN-810: manual lead temperature hot | warm | cool; "" clears it. */
  lead_temperature?: string;
  /** KAN-826: link the client to a company by id; null unlinks. */
  client_company_id?: string | null;
  /** KAN-826: company by name: the server finds or creates it and links; "" unlinks. */
  company?: string;
  /**
   * KAN-889: manual edit of the decision-maker contact. A key with "" removes
   * the value, a missing key is not touched (the UI sends only changed fields); 400 {"lead_contacts": {"<field>": [...]}}.
   */
  lead_contacts?: Partial<LeadContactsPayload>;
}

/** KAN-889: decision-maker contact in the client update body. */
export type LeadContactsPayload = Pick<LeadContact, 'name' | 'role' | 'phone' | 'telegram' | 'email'>;

export interface IdentifyClientPayload {
  phone?: string;
  telegram_id?: number;
  name?: string;
  auto_create?: boolean;
}

export interface IdentifyClientResponse {
  client: Client;
  created: boolean;
}

export interface ClientEstimate {
  id: string;
  title: string;
  total_cost: number;
  currency: string;
  status: string;
  created_at: string;
  updated_at?: string;
  /** Messages in the estimate dialog (0 when the backend does not annotate it). */
  messages_count?: number;
}

export interface PersonalBotStatus {
  connected: boolean;
  bot_username?: string;
  webhook_url?: string;
  webhook_active?: boolean;
}

export interface SetupPersonalBotPayload {
  bot_token: string;
}
